import { ElementType, FunctionComponent, memo, ReactNode } from 'react'

import isEqualReact from 'fast-deep-equal/es6/react'

import config from '@config'
import { AvailableImageCrops, Crops } from '@utils/cook/types'
import useAppTheme from '@hooks/useAppTheme'

import Existing from './Existing'
import Missing from './Missing'
import DefaultPlaceholder, { PlaceholderProps } from './Placeholder'
import { ImageRatios, ImageWidths } from './types'

const {
  image: { missingImageSrc },
} = config

export interface ResponsiveImageV2Props {
  src: string
  alt: string
  ratios: ImageRatios
  crops: Partial<Crops<AvailableImageCrops>>
  widths: ImageWidths
  loading?: HTMLImageElement['loading']
  disablePlaceholder?: boolean
  isLazy?: boolean
  Placeholder?: ElementType<PlaceholderProps>
  className?: string
  children?: ReactNode
  darkModeImage?: Pick<ResponsiveImageV2Props, 'src' | 'crops'>
}

const ResponsiveImageV2: FunctionComponent<ResponsiveImageV2Props> = (
  props,
) => {
  const {
    loading,
    isLazy = true,
    Placeholder = DefaultPlaceholder,
    children,
    darkModeImage,
    src: initialSrc,
    crops: initialCrops,
    ...restOfProps
  } = props

  const appTheme = useAppTheme()
  const { src, crops } =
    appTheme === 'dark' && darkModeImage?.src
      ? { src: darkModeImage.src, crops: darkModeImage.crops }
      : { src: initialSrc, crops: initialCrops }

  if (src === missingImageSrc) {
    return (
      <Missing
        ratios={restOfProps.ratios}
        Placeholder={Placeholder}
        className={restOfProps.className}
      >
        {children}
      </Missing>
    )
  }

  const loadingAttr = loading ?? (isLazy ? 'lazy' : undefined)

  return (
    <Existing
      src={src}
      crops={crops}
      loading={loadingAttr}
      Placeholder={Placeholder}
      {...restOfProps}
    >
      {children}
    </Existing>
  )
}

const MemoizedResponsiveImageV2 = memo(
  ResponsiveImageV2,
  (
    { src: prevSrc, darkModeImage: prevDarkModeImage, children: prevChildren },
    { src: nextSrc, darkModeImage: nextDarkModeImage, children: nextChildren },
  ) =>
    prevSrc === nextSrc &&
    prevDarkModeImage?.src === nextDarkModeImage?.src &&
    isEqualReact(prevChildren, nextChildren),
)

MemoizedResponsiveImageV2.displayName = 'MemoizedResponsiveImageV2'

export default MemoizedResponsiveImageV2
